import Assets from 'assets/assets-tab-icon.svg?react'
import Inspections from 'assets/inspections-tab-icon.svg?react'
import {
  InspectionOverviewEditorGate,
  InventoryOverviewViewerGate,
  PERMISSION_GECKO_INTERNAL_MEMBER,
  PermissionsGate,
  ProcessFlowAdminGate,
} from 'features/auth'
import { ExplorerFeaturePermissionGate } from 'features/drilldown'
import {
  AssetHealthFeaturePermissionGate,
  InspectionSchedulingFeaturePermissionGate,
} from 'features/fde'
import { PARTS_DATABASE_PERMISSIONS_LIST } from 'features/partsDatabase'
import { useRolodexEntries } from 'features/rolodex'
import { useCanVisitSiteView } from 'features/siteView'
import _ from 'lodash'
import {
  Blocks,
  Bot,
  CalendarRange,
  ChartGantt,
  ChartPie,
  ClipboardList,
  GitBranch,
  Heart,
  Map,
  Wrench,
} from 'lucide-react'
import React, { useMemo } from 'react'
import styled, { css } from 'styled-components'

import { SelectedTab } from './Navbar'
import { ImageContainer, StyledLink } from './sharedStyles'

const MidSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto; /* Allow to grow and shrink, taking up available space */
  gap: 0.125rem;
`

const FilledIcon = css`
  height: 1.25rem;
  width: 1.25rem;

  path {
    fill: ${({ theme }) => theme.forecast.tertiaryText};
  }

  &:active {
    path {
      fill: ${({ theme }) => theme.forecast.secondaryText};
    }
  }
`

const OutlinedIcon = css`
  height: 1.25rem;
  width: 1.25rem;

  path {
    background-color: ${({ theme }) => theme.forecast.tertiaryText};
  }

  &:active {
    path {
      background-color: ${({ theme }) => theme.forecast.secondaryText};
    }
  }
`

const AssetsIcon = styled(Assets)`
  ${FilledIcon}
`

const InspectionsIcon = styled(Inspections)`
  ${FilledIcon}
`

const HeartIcon = styled(Heart)`
  ${OutlinedIcon}
`

const MapIcon = styled(Map)`
  ${OutlinedIcon}
`

const CalendarIcon = styled(CalendarRange)`
  ${FilledIcon}
`

const ClipboardIcon = styled(ClipboardList)`
  ${OutlinedIcon}
`

const PieChartIcon = styled(ChartPie)`
  ${OutlinedIcon}
`

const BarChartIcon = styled(ChartGantt)`
  ${OutlinedIcon}
`

const RobotIcon = styled(Bot)`
  ${FilledIcon}
`

const PartsIcon = styled(Blocks)`
  ${OutlinedIcon}
`

const WrenchIcon = styled(Wrench)`
  ${OutlinedIcon}
`

const ProcessFlowIcon = styled(GitBranch)`
  ${OutlinedIcon}
`

export const MidSection: React.FC<{
  isLeftSidebarOpen: boolean
  siteID: string
  selectedTab: SelectedTab
}> = ({ isLeftSidebarOpen, siteID, selectedTab }) => {
  const { sites } = useRolodexEntries(
    useMemo(() => (siteID ? { ids: [siteID] } : []), [siteID])
  )
  const orgId = useMemo(() => _.first(sites)?.tags.organization, [sites])

  const handleNavigate = (path: string, event: React.MouseEvent) => {
    event.stopPropagation()
    if (event.metaKey || event.ctrlKey) {
      window.open(path, '_blank')
      return
    }
  }

  const canVisitSiteView = useCanVisitSiteView()

  return (
    <MidSectionContainer>
      {canVisitSiteView && (
        <StyledLink
          to={`/overview/$siteID`}
          params={{ siteID }}
          $isSelected={selectedTab === 'Overview'}
          onClick={(e: React.MouseEvent) =>
            handleNavigate(`/overview/${siteID}`, e)
          }>
          <ImageContainer>
            <MapIcon />
          </ImageContainer>
          {isLeftSidebarOpen ? <p>Overview</p> : null}
        </StyledLink>
      )}
      <StyledLink
        to='/assets/$siteID'
        params={{ siteID }}
        $isSelected={selectedTab === 'Assets'}
        onClick={(e) => handleNavigate(`/assets/${siteID}`, e)}>
        <ImageContainer>
          <AssetsIcon />
        </ImageContainer>
        {isLeftSidebarOpen ? <p>Assets</p> : null}
      </StyledLink>
      <StyledLink
        to='/inspections/$siteID'
        params={{ siteID }}
        $isSelected={selectedTab === 'Inspections'}
        onClick={(e) => handleNavigate(`/inspections/${siteID}`, e)}>
        <ImageContainer>
          <InspectionsIcon />
        </ImageContainer>
        {isLeftSidebarOpen ? <p>Inspections</p> : null}
      </StyledLink>

      <InspectionSchedulingFeaturePermissionGate>
        <>
          <StyledLink
            to={`/scheduling/$siteID`}
            params={{ siteID }}
            $isSelected={selectedTab === 'Inspection Scheduling'}
            onClick={(e) => handleNavigate(`/scheduling/${siteID}`, e)}>
            <ImageContainer>
              <CalendarIcon />
            </ImageContainer>
            {isLeftSidebarOpen ? <p>Inspection Scheduling</p> : null}
          </StyledLink>
        </>
      </InspectionSchedulingFeaturePermissionGate>
      <AssetHealthFeaturePermissionGate>
        <StyledLink
          to={`/health-plans/$siteID`}
          params={{ siteID }}
          $isSelected={selectedTab === 'Health Plans'}
          onClick={(e) => handleNavigate(`/health-plans/${siteID}`, e)}>
          <ImageContainer>
            <HeartIcon />
          </ImageContainer>
          {isLeftSidebarOpen ? <p>Health Plans</p> : null}
        </StyledLink>
      </AssetHealthFeaturePermissionGate>
      <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
        <StyledLink
          to='/walkdowns/$siteID'
          params={{ siteID }}
          $isSelected={selectedTab === 'Walkdowns'}
          onClick={(e) => handleNavigate(`/walkdowns/${siteID}`, e)}>
          <ImageContainer>
            <ClipboardIcon />
          </ImageContainer>
          {isLeftSidebarOpen ? <p>Walkdowns</p> : null}
        </StyledLink>
      </PermissionsGate>
      {(siteID === 'naes-longridge' || siteID === 'longridge-ac0a01') && (
        <StyledLink
          to='/maximo/$siteID'
          params={{ siteID }}
          $isSelected={selectedTab === 'Maximo'}
          onClick={(e) => handleNavigate(`/maximo/${siteID}`, e)}>
          <ImageContainer>
            <WrenchIcon />
          </ImageContainer>
          {isLeftSidebarOpen ? <p>Maximo</p> : null}
        </StyledLink>
      )}
      <ExplorerFeaturePermissionGate>
        <StyledLink
          to='/explorer/$siteID'
          params={{
            siteID: siteID ?? orgId ?? '',
          }}
          $isSelected={selectedTab === 'Explorer App'}
          onClick={(e) =>
            handleNavigate(`/explorer/${siteID ?? orgId ?? ''}`, e)
          }>
          <ImageContainer>
            <PieChartIcon />
          </ImageContainer>
          {isLeftSidebarOpen ? <p>Explorer</p> : null}
        </StyledLink>
      </ExplorerFeaturePermissionGate>
      <PermissionsGate requires={PARTS_DATABASE_PERMISSIONS_LIST}>
        <StyledLink
          to='/parts/$siteID'
          params={{ siteID }}
          $isSelected={selectedTab === 'Parts Thread'}
          onClick={(e) => handleNavigate(`/parts/${siteID}`, e)}>
          <ImageContainer>
            <PartsIcon />
          </ImageContainer>
          {isLeftSidebarOpen ? <p>Parts Thread</p> : null}
        </StyledLink>
      </PermissionsGate>
      <ProcessFlowAdminGate>
        <StyledLink
          to='/process-flows/$siteID'
          params={{ siteID }}
          $isSelected={selectedTab === 'Process Flow Diagrams'}
          onClick={(e) => handleNavigate(`/process-flows/${siteID}`, e)}>
          <ImageContainer>
            <ProcessFlowIcon />
          </ImageContainer>
          {isLeftSidebarOpen ? <p>Process Flow Diagrams</p> : null}
        </StyledLink>
      </ProcessFlowAdminGate>
      <InspectionOverviewEditorGate>
        <StyledLink
          to='/jobs'
          $isSelected={selectedTab === 'Jobs Overview'}
          onClick={(e) => handleNavigate('/jobs', e)}>
          <ImageContainer>
            <BarChartIcon />
          </ImageContainer>
          {isLeftSidebarOpen ? <p>Jobs Overview</p> : null}
        </StyledLink>
      </InspectionOverviewEditorGate>
      <InventoryOverviewViewerGate>
        <StyledLink
          to='/inventory'
          $isSelected={selectedTab === 'Inventory Management'}
          onClick={(e) => handleNavigate('/inventory', e)}>
          <ImageContainer>
            <RobotIcon />
          </ImageContainer>
          {isLeftSidebarOpen ? <p>Inventory Management</p> : null}
        </StyledLink>
      </InventoryOverviewViewerGate>
    </MidSectionContainer>
  )
}
